import emailjs from '@emailjs/browser';
import { formatConfigForPDF } from './pdfExport';

// EmailJS configuration
// These values are loaded from environment variables
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

/**
 * Initialize EmailJS with public key
 */
export const initEmailJS = () => {
  emailjs.init(EMAILJS_PUBLIC_KEY);
};

/**
 * Format configuration data for email
 * @param {Object} config - Configuration object
 * @returns {string} - Formatted configuration string
 */
const formatConfigForEmail = (config) => {
  const configItems = formatConfigForPDF(config);
  
  return configItems
    .map(item => `${item.label}: ${item.value}`)
    .join('\n');
};

/**
 * Send email with customer request and configuration
 * @param {Object} formData - Customer form data
 * @param {Object} config - Configuration object
 * @param {string} configUrl - URL to the configuration
 * @param {HTMLCanvasElement} canvas - Optional canvas for PDF generation
 * @returns {Promise} - EmailJS send promise
 */
export const sendRequestEmail = async (formData, config, configUrl = '', canvas = null) => {
  try {
    // Initialize EmailJS with public key
    if (EMAILJS_PUBLIC_KEY) {
      emailjs.init(EMAILJS_PUBLIC_KEY);
    } else {
      throw new Error('EmailJS public key not configured');
    }
    
    // Format configuration for email
    const configText = formatConfigForEmail(config);
    
    let pdfNote = '';
    if (canvas) {
      pdfNote = '\n\nNota: È disponibile un PDF con la configurazione 3D. Il cliente può scaricarlo dal configuratore usando il pulsante "Scarica PDF".';
    }
    
    // Prepare email template parameters
    const templateParams = {
      // Customer information
      customer_name: `${formData.firstName} ${formData.lastName}`,
      customer_email: formData.email,
      customer_phone: formData.phone,
      customer_address: `${formData.address}, ${formData.city} ${formData.postalCode}, ${formData.country}`,
      customer_notes: formData.notes || 'Nessuna nota aggiuntiva',
      
      // Configuration details
      configuration_details: configText + pdfNote,
      configuration_url: configUrl || window.location.href,
      
      // Email metadata
      request_date: new Date().toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }),
      
      // Recipient
      // to_email: 'info@pergoleinnovative.it',
      to_email: 'gildack@gmail.com',
      
      // Reply-to
      reply_to: formData.email
    };

    console.log('Sending email with parameters:', {
      serviceId: EMAILJS_SERVICE_ID,
      templateId: EMAILJS_TEMPLATE_ID,
      publicKey: EMAILJS_PUBLIC_KEY ? 'configured' : 'not configured'
    });

    // Send email
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      templateParams
    );

    console.log('Email sent successfully:', response);
    return { success: true, response };
    
  } catch (error) {
    console.error('Email send error:', error);
    
    // Provide more specific error messages
    if (error.message.includes('public key')) {
      throw new Error('Configurazione EmailJS non completa. Verifica la chiave pubblica.');
    } else if (error.message.includes('service')) {
      throw new Error('Servizio email non configurato correttamente.');
    } else if (error.message.includes('template')) {
      throw new Error('Template email non trovato.');
    } else {
      throw new Error('Errore durante l\'invio dell\'email: ' + error.message);
    }
  }
};

/**
 * Send confirmation email to customer
 * @param {Object} formData - Customer form data
 * @param {Object} config - Configuration object
 * @returns {Promise} - EmailJS send promise
 */
export const sendConfirmationEmail = async (formData, config) => {
  try {
    const configText = formatConfigForEmail(config);
    
    const templateParams = {
      customer_name: formData.firstName,
      customer_email: formData.email,
      configuration_details: configText,
      request_date: new Date().toLocaleDateString('it-IT'),
      to_email: formData.email
    };

    // Use a different template for customer confirmation
    const response = await emailjs.send(
      EMAILJS_SERVICE_ID,
      'customer_confirmation_template', // Different template for customer
      templateParams
    );

    return { success: true, response };
    
  } catch (error) {
    console.error('Confirmation email error:', error);
    // Don't throw error for confirmation email failure
    return { success: false, error };
  }
};

/**
 * Validate EmailJS configuration
 * @returns {boolean} - True if configuration is valid
 */
export const isEmailJSConfigured = () => {
  const isConfigured = EMAILJS_SERVICE_ID && 
                      EMAILJS_TEMPLATE_ID && 
                      EMAILJS_PUBLIC_KEY &&
                      EMAILJS_SERVICE_ID.trim() !== '' &&
                      EMAILJS_TEMPLATE_ID.trim() !== '' &&
                      EMAILJS_PUBLIC_KEY.trim() !== '';
  
  // Debug logging
  console.log('EmailJS Configuration Status:', {
    serviceId: EMAILJS_SERVICE_ID ? `${EMAILJS_SERVICE_ID.substring(0, 8)}...` : 'NOT SET',
    templateId: EMAILJS_TEMPLATE_ID ? `${EMAILJS_TEMPLATE_ID.substring(0, 8)}...` : 'NOT SET',
    publicKey: EMAILJS_PUBLIC_KEY ? `${EMAILJS_PUBLIC_KEY.substring(0, 8)}...` : 'NOT SET',
    isConfigured
  });
  
  return isConfigured;
};

/**
 * Get EmailJS setup instructions
 * @returns {string} - Setup instructions
 */
export const getSetupInstructions = () => {
  return `
    Per configurare EmailJS:
    
    1. Crea un account su https://www.emailjs.com/
    2. Configura un servizio email (Gmail, Outlook, etc.)
    3. Crea un template email con i seguenti parametri:
       - {{customer_name}}
       - {{customer_email}}
       - {{customer_phone}}
       - {{customer_address}}
       - {{customer_notes}}
       - {{configuration_details}}
       - {{configuration_url}}
       - {{request_date}}
    4. Aggiungi le variabili nel file .env:
       - REACT_APP_EMAILJS_SERVICE_ID=your_service_id
       - REACT_APP_EMAILJS_TEMPLATE_ID=your_template_id
       - REACT_APP_EMAILJS_PUBLIC_KEY=your_public_key
  `;
};